import { DetectionClass } from "@/constants/terminal/detection-class"
import { detectionTypeLabel } from "@/lib/terminal/detection-type-label"
import {
  detectionMeaningsByClass,
  exportDetectionItem,
} from "@/lib/terminal/terminal-definitions"

// Home's Reports (Portfolio screen, Dhanur, 25-26 Oct 2026). A Report is a
// named list of existing Detection types and nothing else: a Company is in it
// when its Delivered File carries an active Detection of one of them inside
// the three-year window (home-reports.ts), with no score filter and no
// threshold of ours. Each type sits in one Report. Every type here is one the
// policy catalog emits (policies/detections/1.0.json).
//
// The header is read at a glance, not as prose (Dhanur, 28 Oct): a lead line
// saying who is on the list and over what window, then the criteria, one per
// chip the table's Detections column wears, each with its meaning from the
// Definitions page. Every name and lead is a working draft for Dhanur except
// "Not in Good Standing" (Dhanur, 26 Oct).
export enum HomeReport {
  OFFICIAL_ACTIONS = "official_actions",
  PENDING_MATTERS = "pending_matters",
  FINANCIAL_STRESS = "financial_stress",
  NOT_IN_GOOD_STANDING = "not_in_good_standing",
  CUSTOMER_HARM = "customer_harm",
}

// One chip: its types share the label detectionTypeLabel gives them, and its
// meaning is the Definitions page's for that label in this class
// (terminal-definitions.ts), never a second copy here.
export type HomeReportCriterion = {
  detectionClass: DetectionClass
  detectionTypeKeys: string[]
}

type HomeReportData = {
  label: string
  lead: string
  criteria: HomeReportCriterion[]
}

// Each Report's types are its criteria's, in order.
const withTypeKeys = (data: HomeReportData) => ({
  ...data,
  detectionTypeKeys: data.criteria.flatMap(
    ({ detectionTypeKeys }) => detectionTypeKeys
  ),
})

export const dataByHomeReport: Record<
  HomeReport,
  ReturnType<typeof withTypeKeys>
> = {
  [HomeReport.OFFICIAL_ACTIONS]: withTypeKeys({
    label: "Official Actions",
    lead: "A licensing board, OSHA or a government authority ruled against the company in the last three years.",
    criteria: [
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: [
          "license_operating_restriction",
          "license_disciplinary_order",
        ],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: [
          "qualifier_license_restriction",
          "qualifier_license_order",
        ],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["license_bond_restriction"],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["license_admonishment"],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["workplace_safety_citation"],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["government_enforcement_order"],
      },
    ],
  }),
  [HomeReport.PENDING_MATTERS]: withTypeKeys({
    label: "Pending Matters",
    lead: "Opened against the company in the last three years and not yet decided.",
    criteria: [
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: [
          "pending_civil_proceeding",
          "pending_consumer_lawsuits",
        ],
      },
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["license_pending_discipline"],
      },
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["license_pending_complaint"],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["government_enforcement_action"],
      },
    ],
  }),
  [HomeReport.FINANCIAL_STRESS]: withTypeKeys({
    label: "Financial Stress",
    lead: "Creditors, courts or tax authorities pursued the company in the last three years.",
    criteria: [
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["tax_lien"],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["judgment_lien"],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["civil_judgment"],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["bankruptcy_proceeding"],
      },
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["stacked_secured_claims"],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["creditor_default_cluster"],
      },
    ],
  }),
  [HomeReport.NOT_IN_GOOD_STANDING]: withTypeKeys({
    label: "Not in Good Standing",
    lead: "As of our last check, the company may not be authorized to do the work, or may have stopped doing it.",
    criteria: [
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["entity_standing_impairment"],
      },
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["license_qualification_lapse"],
      },
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["license_administrative_suspension"],
      },
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["entity_recycling"],
      },
      {
        detectionClass: DetectionClass.FIRE,
        detectionTypeKeys: ["operational_cessation"],
      },
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["activity_collapse"],
      },
    ],
  }),
  [HomeReport.CUSTOMER_HARM]: withTypeKeys({
    label: "Customer Harm",
    lead: "Customers keep reporting the same harm, in reviews and BBB complaints over the last three years. One complaint never counts.",
    criteria: [
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["customer_harm_pattern"],
      },
      {
        detectionClass: DetectionClass.SMOKE,
        detectionTypeKeys: ["customer_reported_deception_pattern"],
      },
    ],
  }),
}

// A criterion's chip, and its meaning as the Definitions page gives it.
const criterionType = ({ detectionTypeKeys }: HomeReportCriterion) => ({
  typeKey: detectionTypeKeys[0],
  typeLabel: "",
})

export const criterionLabel = (criterion: HomeReportCriterion) =>
  detectionTypeLabel(criterionType(criterion))

export const criterionMeaning = (criterion: HomeReportCriterion): string =>
  detectionMeaningsByClass[criterion.detectionClass][
    exportDetectionItem(criterionType(criterion))
  ]
